import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "../../lib/api";
import { PricingCard } from "../PricingCard";
import { countryInfo } from "../../constants/options";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

const CURRENCIES = [
  { code: "gbp", label: "£10 GBP", price: "£10" },
  { code: "usd", label: "$14 USD", price: "$14" },
  { code: "eur", label: "€12 EUR", price: "€12" },
];
const LIVE = ["active", "trialing", "past_due"];

const statusText = (p, subscribed, price) => {
  const trialing = p.subscription_status === "trialing";
  if (subscribed && trialing && p.cancel_at_period_end) return `Your free trial is cancelled. Your profile stays live until ${formatDate(p.renews_on)} and you won't be charged anything.`;
  if (subscribed && p.cancel_at_period_end) return `Your subscription is cancelled. Your profile stays live until ${formatDate(p.renews_on)}, then it will be removed from search. You won't be charged again.`;
  if (subscribed && p.subscription_status === "past_due") return "Your last renewal payment failed. Stripe will retry your card — please make sure it's up to date.";
  if (subscribed && trialing) return `You're on your free trial until ${formatDate(p.renews_on)}. Your first yearly payment of ${price} is taken then, and yearly after that. Cancel any time before and you won't be charged.`;
  if (subscribed) return `Your subscription renews automatically on ${formatDate(p.renews_on)}. You can cancel any time and stay live until then.`;
  if (p.free_year && p.active) return `You're on a free first year until ${formatDate(p.paid_until)}. Add your card any time to stay listed afterwards — nothing is charged until your free time ends.`;
  if (p.paid_until && p.stripe_subscription_id) return `Your listing ended on ${formatDate(p.paid_until)}. Subscribe again to reappear in search.`;
  return `Start your 6 months FREE to make your page at extras.tv/yourname visible in search, ready to share in Facebook groups and beyond. Add your card now — nothing is taken for 6 months, then ${price} a year. Cancel any time.`;
};

const CancelButton = ({ onConfirm, busy, trialing }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} className="w-full border border-white/20 py-4 font-bold uppercase tracking-wider text-sm hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-60" data-testid="membership-cancel-button">
        Cancel subscription
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-[hsl(0,0%,7%)] border-white/10" data-testid="membership-cancel-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
        <AlertDialogDescription>{trialing ? "You won't be charged anything. Your profile stays live until your free trial ends, then it's removed from search." : "You won't be charged again. Your profile stays live until the end of the year you've paid for, then it's removed from search."}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="membership-cancel-keep">Keep subscription</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-500 hover:bg-red-400 text-white" data-testid="membership-cancel-confirm">Yes, cancel</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Membership = ({ profile, onChange }) => {
  const [busy, setBusy] = useState(false);
  const subscribed = Boolean(profile.stripe_subscription_id) && LIVE.includes(profile.subscription_status);
  const firstTime = !profile.stripe_subscription_id;
  const freeYear = firstTime && profile.free_year && profile.active;
  const [currency, setCurrency] = useState(profile.subscription_currency || countryInfo(profile.country).currency);
  const current = CURRENCIES.find((c) => c.code === (subscribed ? profile.subscription_currency || "gbp" : currency)) || CURRENCIES[0];

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, currency });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const change = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      onChange(data);
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-12 gap-10" data-testid="membership-panel">
      <div className="md:col-span-6">
        <h2 className="font-display text-3xl font-bold" data-testid="membership-heading">
          {subscribed ? (profile.subscription_status === "trialing" ? (profile.cancel_at_period_end ? "Free trial cancelled" : "Free trial") : profile.cancel_at_period_end ? "Subscription cancelled" : "Subscribed") : profile.free_year && profile.active ? "Free first year" : firstTime ? "6 months free" : "Subscribe & go live"}
        </h2>
        <p className="text-white/60 mt-4" data-testid="membership-status-text">{statusText(profile, subscribed, current.price)}</p>
        <p className="text-white/40 text-sm mt-6">Billed securely by Stripe. {freeYear ? "Nothing is charged until your free year ends. " : firstTime ? "Nothing is charged for the first 6 months. " : ""}Renews automatically each year until you cancel.</p>
      </div>
      <div className="md:col-span-5 md:col-start-8">
        <PricingCard testId="membership-pricing-card" price={current.price} showTrial={firstTime && !freeYear}>
          {subscribed ? (
            profile.cancel_at_period_end ? (
              <button onClick={() => change("/subscription/resume", "Subscription resumed")} disabled={busy} className="w-full bg-primary text-primary-foreground py-4 font-bold uppercase tracking-wider text-sm hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="membership-resume-button">
                Resume subscription
              </button>
            ) : (
              <CancelButton busy={busy} trialing={profile.subscription_status === "trialing"} onConfirm={() => change("/subscription/cancel", profile.subscription_status === "trialing" ? "Free trial cancelled" : "Subscription cancelled")} />
            )
          ) : (
            <>
              <p className="label-xs mb-3">Pay in</p>
              <div className="grid grid-cols-3 border border-white/15 mb-4" data-testid="membership-currency-picker">
                {CURRENCIES.map((c) => (
                  <button key={c.code} type="button" onClick={() => setCurrency(c.code)} className={`py-2.5 text-sm font-bold transition-colors ${currency === c.code ? "bg-white text-black" : "text-white/70 hover:text-white"}`} data-testid={`currency-option-${c.code}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <button onClick={subscribe} disabled={busy} className="w-full bg-primary text-primary-foreground py-4 font-bold uppercase tracking-wider text-sm hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="membership-pay-button">
                {busy ? "Redirecting to payment…" : freeYear ? `Add card — free until ${formatDate(profile.free_until)}` : firstTime ? "Start 6 months free" : `Subscribe — ${current.price} / year`}
              </button>
            </>
          )}
        </PricingCard>
      </div>
    </div>
  );
};
